<template>
  <div
    ref="scrollArea"
    class="mt-20"
    :class="{
      'manual-merges-dialog-table': pagedCandidates.length > 3,
      'shadow-top': !isAtTop,
      'shadow-bottom': !isAtBottom
    }"
  >
    <AppScrollableTable
      class="table-bordered table-rounded my-0"
      :class="{ 'table-error': error }"
      :has-fixed-column="false"
    >
      <AppTable
        hoverable
        :table-data="pagedDisplayCandidates"
        :loading="loading"
        @clickRow="selectRow"
      >
        <AppTableColumn
          header=""
          name="select"
        >
          <template #default="{ row }">
            <input
              v-if="!row.isDetail"
              v-model="localSelectedId"
              type="radio"
              :value="row.id"
              class="merge-radio"
              :disabled="row.dup_merge_in_progress"
              :data-testid="`merge-candidate-radio-${row.user.id}`"
            />
          </template>
        </AppTableColumn>

        <AppTableColumn
          header="Member"
          name="member"
        >
          <template #default="{ row }">
            <template v-if="!row.isDetail">
              <div
                class="d-flex align-items-center gap-5"
                :data-testid="`merge-candidate-row-${row.user.id}`"
              >
                <i
                  :class="[
                    { invisible: row.devices_count <= 1 },
                    'hi',
                    'hi-right',
                    'hi-12',
                    'clickable',
                    'mr-10',
                    { 'arrow-expanded': isRowExpanded(row.id) },
                  ]"
                  @click.prevent="row.devices_count > 1 && toggleRow(row.id)"
                />
                <HubAvatar
                  :user-id="row.user.id"
                  avatar-style="medium"
                  :users-avatars="usersAvatars"
                />
                <div>
                  <div>{{ row.user.name }}</div>
                  <div class="small text-lighter">{{ row.os_username }}</div>
                  <div class="small text-lighter">{{ row.user.email }}</div>
                  <div class="small text-lighter">({{ row.member_type }} member)</div>
                </div>
              </div>
            </template>
          </template>
        </AppTableColumn>

        <AppTableColumn
          header="Device"
          name="device"
        >
          <template #default="{ row }">
            <MergeCandidatesExpandableCell
              :items="row.devices"
              :detail="row.isDetail"
              label="Computers"
            >
              <template #single="{ item }">
                {{ item.machine_name }}<br />
                <span class="text-lighter">{{ item.os_platform }} ({{ item.os_version }})</span>
              </template>

              <template #detail="{ items }">
                <div
                  v-for="(d, idx) in items"
                  :key="idx"
                  class="details-container"
                >
                  {{ d.machine_name }}<br />
                  <span class="text-lighter">{{ d.os_platform }} ({{ d.os_version }})</span>
                </div>
              </template>
            </MergeCandidatesExpandableCell>
          </template>
        </AppTableColumn>

        <AppTableColumn
          header="Date added"
          name="date_added"
        >
          <template #default="{ row }">
            <MergeCandidatesExpandableCell
              :items="row.devices"
              :detail="row.isDetail"
              label="Dates"
            >
              <template #single="{ item }">
                {{ item.added_at }}<br />
                <span class="text-lighter">{{ item.added_time }} {{ item.time_zone_abbr }}</span>
              </template>
              <template #detail="{ items }">
                <div
                  v-for="(d, idx) in items"
                  :key="idx"
                  class="details-container"
                >
                  {{ d.added_at }}<br />
                  <span class="text-lighter">{{ d.added_time }} {{ d.time_zone_abbr }}</span>
                </div>
              </template>
            </MergeCandidatesExpandableCell>
          </template>
        </AppTableColumn>

        <AppTableColumn
          header="Billing status"
          name="billing_status"
          :header-tooltip="billingGraceTooltip"
        >
          <template #default="{ row }">
            <template v-if="!row.isDetail">
              <div class="d-flex">
                <div class="d-flex flex-column align-items-center">
                  <span
                    :class="[
                      'label',
                      row.billing_status === 'Billed' ? 'label-success-lighter' : 'label-danger-lighter',
                    ]"
                  >
                    {{ row.billing_status }}
                  </span>
                  <div
                    v-if="row.days_left"
                    class="small text-lighter mt-5"
                  >
                    {{ row.days_left }} {{ row.days_left | pluralize('day') }} left
                  </div>
                </div>
              </div>
            </template>
          </template>
        </AppTableColumn>

        <AppTableColumn
          header="App version"
          name="app_version"
        >
          <template #default="{ row }">
            <template v-if="row.isDetail">
              <div class="version-details">
                <div
                  v-for="(d, idx) in row.devices"
                  :key="idx"
                  class="details-container mb-5"
                >
                  {{ d.client_version }}<br />
                </div>
              </div>
            </template>
            <template v-else>
              <template v-if="row.app_versions_count && row.app_versions_count > 1">
                <div class="d-flex align-items-center gap-5">
                  <span class="badge badge-primary-lighter">{{ row.app_versions_count }}</span>
                  <strong>Versions</strong>
                </div>
              </template>
              <template v-else>
                {{ row.app_version || '—' }}
              </template>
            </template>
          </template>
        </AppTableColumn>
      </AppTable>
    </AppScrollableTable>

    <AppPagination
      v-if="pagination && pagination.total_pages > 1"
      :pagination="pagination"
      :resource="{ one: 'member', many: 'members' }"
      @page-changed="onPageChanged"
    />
  </div>
</template>
<script>
import MergeCandidatesExpandableCell from '~/components/client_agents/MergeCandidatesExpandableCell.vue';
import { createUsersAvatarsMixin } from '~/mixins/UsersAvatarsMixin';
import CorpAppBillingMixin from '~/mixins/CorpAppBillingMixin';

export default {
  name: 'ClientAgentMergeCandidatesTable',
  components: { MergeCandidatesExpandableCell },
  mixins: [createUsersAvatarsMixin('avatarUsers'), CorpAppBillingMixin],
  props: {
    candidates: { type: Array, default: () => [] },
    pagination: { type: Object, default: null },
    selectedId: { type: [Number, String], default: null },
    error: { type: Boolean, default: false },
    searching: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      localSelectedId: this.selectedId,
      page: 1,
      perPage: 20,
      expandedRows: [],
      isAtTop: true,
      isAtBottom: true,
    };
  },
  computed: {
    totalPages() {
      return this.pagination ? this.pagination.total_pages : Math.ceil(this.candidates.length / this.perPage) || 1;
    },
    pagedCandidates() {
      return this.candidates;
    },
    pagedDisplayCandidates() {
      return this.pagedCandidates.flatMap((c) => {
        const candidateRow = { ...c };
        const classes = [];

        if (c.dup_merge_in_progress) {
          classes.push('row-merging');
        }
        if (this.expandedRows.includes(c.id)) {
          classes.push('row-expanded');
        }

        if (classes.length) {
          candidateRow.trClasses = classes;
        }

        const rows = [candidateRow];

        if (this.expandedRows.includes(c.id)) {
          rows.push({
            id: `${c.id}-details`,
            isDetail: true,
            parentId: c.id,
            devices: c.devices,
            devices_count: c.devices_count,
            added_at: c.added_at,
            added_time: c.added_time,
            time_zone_abbr: c.time_zone_abbr,
            app_versions_count: c.app_versions_count,
            app_version: c.app_version,
            trClasses: ['detail-row'],
          });
        }

        return rows;
      });
    },
    avatarUsers() {
      return this.candidates.map((c) => ({ id: c.user.id, avatar: c.user.avatar_url }));
    },
  },
  watch: {
    localSelectedId(val) {
      this.$emit('update:selected-id', val);
    },
    selectedId(val) {
      this.localSelectedId = val;
    },
    candidates() {
      this.page = 1;
      this.$nextTick(this.updateScrollShadows);
    },
  },
  mounted() {
    this.$nextTick(this.updateScrollShadows);
    this.$refs.scrollArea?.addEventListener('scroll', this.updateScrollShadows);
  },
  updated() {
    this.$nextTick(this.updateScrollShadows);
  },
  beforeDestroy() {
    this.$refs.scrollArea?.removeEventListener('scroll', this.updateScrollShadows);
  },
  methods: {
    onPageChanged(page) {
      this.page = page;
      this.$emit('page-changed', page);
      this.$nextTick(this.updateScrollShadows);
    },
    updateScrollShadows() {
      const el = this.$refs.scrollArea;

      if (!el) {return;}
      this.isAtTop = el.scrollTop === 0;
      this.isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    },
    toggleRow(id) {
      if (this.expandedRows.includes(id)) {
        this.expandedRows = this.expandedRows.filter((i) => i !== id);
      } else {
        this.expandedRows.push(id);
      }
      this.$nextTick(this.updateScrollShadows);
    },
    isRowExpanded(id) {
      return this.expandedRows.includes(id);
    },
    selectRow(row) {
      if (row.isDetail || row.dup_merge_in_progress) {return;}
      this.localSelectedId = row.id;
    },
  },
};
</script>
